import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowRight,
  CheckCircle2,
  Layers3,
  Sparkles,
  Target,
  Trophy,
} from "lucide-react";

/* ================= LAYOUT ================= */

const Page = styled.section`
  min-height: 100vh;
  padding: 120px 24px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const ResultHero = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
  margin-bottom: 28px;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 22px;
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: clamp(34px, 5vw, 44px);
  font-weight: 900;
  line-height: 1.15;
  margin-bottom: 18px;
  letter-spacing: -0.5px;
  color: #f8fafc;
`;

const Sub = styled.p`
  color: #9ca3af;
  max-width: 760px;
  font-size: 15px;
  line-height: 1.7;
`;

const Highlight = styled.span`
  color: #f59e0b;
  font-weight: 800;
`;

const SummaryPanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const SummaryLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 1000;
`;

const SummaryList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 6px;
  color: #f8fafc;
`;

/* ================= CARDS ================= */

const Card = styled.div`
  padding: 30px;
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.78), rgba(2, 6, 23, 0.72)),
    rgba(15, 23, 42, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.12);
  margin-bottom: 26px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
`;

const InsightsGrid = styled.div`
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ReasonList = styled.ul`
  margin-top: 12px;
  padding-left: 18px;
`;

const ReasonItem = styled.li`
  color: #9ca3af;
  margin-bottom: 6px;
  font-size: 15px;
`;

/* ================= CTA ================= */

const FreeRoadmapCard = styled(Card)`
  border: 1px solid rgba(245, 158, 11, 0.26);
  background: linear-gradient(
    180deg,
    rgba(245, 158, 11, 0.12),
    rgba(56, 189, 248, 0.04)
  );
`;

const ValueGrid = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 16px;
`;

const ValueItem = styled.div`
  font-size: 15px;
  color: #9ca3af;
`;

const CTAButton = styled.button`
  margin-top: 22px;
  width: 100%;
  padding: 16px;
  border-radius: 16px;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  border: none;
  color: white;
  background: linear-gradient(90deg, #f59e0b, #d97706);
  box-shadow: 0 18px 60px rgba(245, 158, 11, 0.32);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

/* ================= LOGIC ================= */

function formatTech(key) {
  if (!key) return null;
  return key.replace("-", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function calculateResult({ path, profile }) {
  const { stage, experience, interest, technology, job_intent } =
    profile.normalized || {};

  let direction = "";
  let level = "Beginner";

  const focus = [];
  const reasons = [];
  const valueItems = [];

  /* ================= LEVEL ================= */

  if (experience?.includes("completely new")) {
    level = "Absolute Beginner";
    focus.push("Programming fundamentals from scratch");
  }

  if (experience?.includes("some basics")) {
    level = "Beginner";
  }

  /* ================= PATH ================= */

  if (path === "product") {
    valueItems.push("Project-driven, job-ready roadmap");
  }

  if (path === "dsa") {
    direction = "DSA / Interview-focused Engineer";
    focus.push("Daily DSA practice with patterns");
    valueItems.push("Interview-first preparation");
  }

  /* ================= INTEREST ================= */

  if (interest?.includes("Mobile")) {
    direction = "Mobile App Developer";
    reasons.push("Interest in Android / iOS applications");
    focus.push("Mobile UI + app logic + real apps");
  }

  if (interest?.includes("Frontend")) {
    direction = "Frontend Developer";
    reasons.push("Interest in UI and user experience");
    focus.push("Frontend fundamentals + projects");
  }

  if (interest?.includes("Backend")) {
    direction = "Backend Developer";
    reasons.push("Interest in APIs and system logic");
    focus.push("Backend fundamentals + databases");
  }

  if (interest?.includes("confused")) {
    direction = "Foundation Engineer";
    reasons.push("Exploring before specialization");
    focus.push("Core programming + exposure to stack");
  }

  /* ================= TECHNOLOGY ================= */

  if (technology) {
    const techName = technology.replace("-", " ").toUpperCase();
    reasons.push(`Starting with ${techName}`);
    focus.push(`Hands-on learning using ${techName}`);
  }

  /* ================= JOB INTENT ================= */

  if (job_intent?.includes("soon")) {
    reasons.push("Strong intention to get a job quickly");
    focus.push("Job-ready projects + interview basics");
  }

  /* ================= DEFAULT SAFETY ================= */

  if (!direction) direction = "Software Developer";
  if (reasons.length === 0) reasons.push("Based on your learning goals");

  valueItems.push("Beginner → Intermediate → Job-ready phases");
  valueItems.push("Clear daily learning & projects");
  valueItems.push("No confusion, only execution");

  return { direction, level, focus, reasons, valueItems };
}

/* ================= COMPONENT ================= */

export default function CareerClarityResult() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const selectedPath = state?.path || "product";
  const selectedTech = state?.technology || null;
  const normalized = state?.normalizedAnswers || {};
  const profile = state?.normalizedAnswers?.profile || {};

  if (!state?.normalizedAnswers?.profile) {
    return (
      <Page>
        <Container>
          <ResultHero>
            <Eyebrow>
              <Sparkles size={14} />
              Career Clarity Result
            </Eyebrow>
            <Title>No result found yet</Title>
            <Sub>
              Complete the clarity flow first so we can generate a direction,
              level, and roadmap recommendation for you.
            </Sub>
            <CTAButton onClick={() => navigate("/career-clarity")}>
              Start Career Clarity Test
              <ArrowRight size={18} />
            </CTAButton>
          </ResultHero>
        </Container>
      </Page>
    );
  }

  const result = calculateResult({
    path: selectedPath,
    profile: state?.normalizedAnswers?.profile,
  });

  return (
    <Page>
      <Container>
        <ResultHero>
          <HeroGrid>
            <div>
              <Eyebrow>
                <Sparkles size={14} />
                Career Clarity Result
              </Eyebrow>
              <Title>Your best direction is clearer now</Title>
              <Sub>
                Based on your answers, goals, and current preferences,
                <Highlight> this is the direction that makes the most sense for you right now.</Highlight>
              </Sub>
            </div>

            <SummaryPanel>
              <SummaryLabel>Recommended path</SummaryLabel>
              <SummaryValue>{result.direction}</SummaryValue>
              <SummaryList>
                <SummaryItem>
                  <Trophy size={16} />
                  Current level: {result.level}
                </SummaryItem>
                <SummaryItem>
                  <Layers3 size={16} />
                  {selectedPath === "dsa"
                    ? "Interview-first preparation path"
                    : "Roadmap-first execution path"}
                </SummaryItem>
                <SummaryItem>
                  <Target size={16} />
                  {selectedTech ? `Start with ${formatTech(selectedTech)}` : "Start with the recommended core foundation"}
                </SummaryItem>
              </SummaryList>
            </SummaryPanel>
          </HeroGrid>
        </ResultHero>

        <InsightsGrid>
          <Card>
            <CardTitle>Recommended Career Direction</CardTitle>
            <p style={{ color: "#c7d2fe", fontSize: 20, fontWeight: 800 }}>
              {result.direction}
            </p>

            <ReasonList>
              {result.reasons.map((r, i) => (
                <ReasonItem key={i}>{r}</ReasonItem>
              ))}
            </ReasonList>
          </Card>

          <Card>
            <CardTitle>Current Level</CardTitle>
            <p style={{ color: "#c7d2fe", marginTop: 8, fontSize: 18, fontWeight: 800 }}>
              {result.level}
            </p>
            <Sub style={{ marginTop: 10, maxWidth: "100%" }}>
              This is your current starting point, not your limit. The roadmap
              will be built around this level.
            </Sub>
          </Card>

          <Card>
            <CardTitle>Next 60-90 Day Focus</CardTitle>
            <ul style={{ marginTop: 12, paddingLeft: 18 }}>
              {result.focus.map((item, i) => (
                <li key={i} style={{ color: "#9ca3af", marginBottom: 8 }}>
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        </InsightsGrid>

        <FreeRoadmapCard>
          <CardTitle>Your Personalized Roadmap</CardTitle>

          <ValueGrid>
            {result.valueItems.map((v, i) => (
              <ValueItem key={i}>• {v}</ValueItem>
            ))}
          </ValueGrid>

          <CTAButton
            onClick={() =>
              navigate("/create-roadmap", {
                state: {
                  path: selectedPath,
                  technology: selectedTech,
                  clarityResult: result,
                  profile: profile,
                },
              })
            }
          >
            Generate My Roadmap
            <ArrowRight size={18} />
          </CTAButton>
        </FreeRoadmapCard>
      </Container>
    </Page>
  );
}
